import React, { Fragment, useEffect, useRef, useState } from 'react';

import { Modal } from 'flowbite';
import { isNull } from 'lodash';

import PayrateModal from './PayrateModal';

import { formatFigure, formatToFixedMoneyWithLocale } from 'Helpers/formatService';
import { modalOptions } from 'Helpers/modalOptions';

import Button from 'CommonComponents/Button';
import SimpleConfirmationModal from 'CommonComponents/SimpleConfirmationModal';
import TextButton from 'CommonComponents/TextButton';

import { useToast } from 'Hooks/useToast';

import EditIcon from 'Assets/icons/edit.svg';
import TrashIcon from 'Assets/icons/trash.svg';

export default function WorkforceAssumptionPayrates(props) {
    const [editedPayrate, setEditedPayrate] = useState(null);
    const [deletedPayrate, setDeletedPayrate] = useState(null);

    const [cratePayrateModalElement, setCratePayrateModalElement] = useState(null);
    const [deletePayrateModalElement, setDeletePayrateModalElement] = useState(null);

    const cratePayrateModalRef = useRef();
    const deletePayrateModalRef = useRef();

    const toast = useToast();

    useEffect(() => {
        cratePayrateModalRef && setCratePayrateModalElement(new Modal(cratePayrateModalRef.current, modalOptions(false)));
        deletePayrateModalRef && setDeletePayrateModalElement(new Modal(deletePayrateModalRef.current, modalOptions(false)));
    }, []);

    useEffect(() => {
        if (editedPayrate) {
            showCreatePayrateModal();
        }
        if (deletedPayrate) {
            showDeleteModal();
        }
    }, [editedPayrate, deletedPayrate]);

    const groups = [
        { key: 'inflation', name: $t('Yearly Inflation') },
        { key: 'bonus', name: $t('Yearly Bonus') },
        { key: 'onCosts', name: $t('On Costs') },
    ];

    const calculateColSpan = (group) => {
        if (!props.defaultPercentage[group]) {
            return 1;
        }

        return Object.keys(props.defaultPercentage[group]).length;
    };

    const calculateWidth = (group) => {
        if (!props.defaultPercentage[group]) {
            return '60px';
        }

        const width = Object.keys(props.defaultPercentage[group]).length * 60;

        return `${width}px`;
    };

    const showCreatePayrateModal = () => {
        setDeletedPayrate(null);
        cratePayrateModalElement && cratePayrateModalElement.show();
    };

    const showDeleteModal = () => {
        setEditedPayrate(null);
        deletePayrateModalElement && deletePayrateModalElement.show();
    };

    const closeModal = () => {
        setEditedPayrate(null);
        setDeletedPayrate(null);
        cratePayrateModalElement && cratePayrateModalElement.hide();
        deletePayrateModalElement && deletePayrateModalElement.hide();
    };

    const editPayrate = (item) => {
        setEditedPayrate(item);
    };
    const deletePayrate = (item) => {
        setDeletedPayrate(item);
    };

    const defaultPrecentagesValue = () => {
        const result = JSON.parse(JSON.stringify(props.defaultPercentage));
        if (editedPayrate) {
            Object.keys(props.defaultPercentage).forEach((group) => {
                Object.keys(props.defaultPercentage[group]).forEach((year) => {
                    if (!isNull(result[group][year])) {
                        const value =
                            editedPayrate.percentage[group][year] !== undefined ? editedPayrate.percentage[group][year] : props.defaultPercentage[group][year];

                        result[group][year] = value;
                    }
                });
            });
        }

        return result;
    };

    const saveUrl = () => {
        return editedPayrate ? props.updatePayrateUrl.replace('PAYRATE_ID', editedPayrate.id) : props.storeUrl;
    };

    const deleteUrl = () => {
        return deletedPayrate ? props.deletePayrateUrl.replace('PAYRATE_ID', deletedPayrate.id) : '';
    };

    const removePayrate = () => {
        axios
            .delete(deleteUrl(), {})
            .then((response) => {
                toast.success($t('Payrate successfully removed'));
                props.fetchPayrates();
                closeModal();
            })
            .catch((err) => {
                toast.error(err.response?.data?.message || $t('Something went wrong while deleting the payrate'));
            });
    };

    return (
        <div className="tw-space-y-2 tw-my-3">
            <div className="tw-grid tw-grid-cols-1 tw-gap-2">
                <div>
                    <div className="tw-px-6 tw-py-6.5 tw-rounded-lg tw-bg-white tw-shadow-primary">
                        <div className="tw-flex tw-justify-between tw-items-start tw-mb-5">
                            <h3 className="table__header">{$t('Pay Rates')}</h3>
                            <Button variant="primary" onClick={showCreatePayrateModal}>
                                {$t('Add Pay Rate')}
                            </Button>
                        </div>

                        {Object.keys(props.defaultPercentage).length > 0 && (
                            <div className="inbud-table_wrapper tw-overflow-y-auto tw-max-h-screen">
                                <table className="inbud-table tw-table-fixed inbud-table_first-sticky">
                                    <thead className="tw-sticky tw-top-0 tw-z-10">
                                        <tr className="tw-capitalize">
                                            <th className="tw-w-56" rowSpan={2} style={{ zIndex: `10` }}>
                                                {$t('Pay Rate - Level Description')}
                                            </th>
                                            <th className="tw-w-36" rowSpan={2}>
                                                {$t('Minimum')}
                                            </th>
                                            <th className="tw-w-36" rowSpan={2}>
                                                {$t('Maximum')}
                                            </th>
                                            <th className="tw-w-36" rowSpan={2}>
                                                {$t('Average')}
                                            </th>
                                            {groups.map((group) => {
                                                return (
                                                    <th
                                                        key={group.key}
                                                        className="tw-border-x tw-border-x-white"
                                                        colSpan={calculateColSpan(group.key)}
                                                        style={{ width: calculateWidth(group.key) }}
                                                    >
                                                        {group.name}
                                                    </th>
                                                );
                                            })}
                                            <th className="tw-w-24" rowSpan={2}>
                                                {$t('Actions')}
                                            </th>
                                        </tr>
                                        <tr>
                                            {groups.map((group) => {
                                                const groupKey = group.key;
                                                const groupData = props.defaultPercentage[groupKey];

                                                return (
                                                    <Fragment key={`year-${groupKey}`}>
                                                        {Object.keys(groupData).map((year, index) => {
                                                            let border = '';
                                                            if (index === 0) {
                                                                border = 'tw-border-l tw-border-l-white';
                                                            }
                                                            if (index === Object.keys(groupData).length - 1) {
                                                                border = 'tw-border-r tw-border-r-white';
                                                            }

                                                            return (
                                                                <th className={`${border}`} key={`year-${group}-${year}`}>
                                                                    {year}
                                                                </th>
                                                            );
                                                        })}
                                                    </Fragment>
                                                );
                                            })}
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {props.payrates.map((payrate) => {
                                            return (
                                                <tr key={payrate.id}>
                                                    <td>{payrate.name}</td>
                                                    <td className="inbud-numeric-cell">{formatFigure(payrate.min)}</td>
                                                    <td className="inbud-numeric-cell">{formatFigure(payrate.max)}</td>
                                                    <td className="inbud-numeric-cell">{formatFigure((payrate.min + payrate.max) / 2)}</td>
                                                    {groups.map((group) => {
                                                        const groupKey = group.key;
                                                        const groupData = payrate.percentage[groupKey];

                                                        return (
                                                            <Fragment key={`year-${groupKey}-data`}>
                                                                {Object.keys(groupData).map((year) => {
                                                                    return (
                                                                        <td key={`year-${group}-data-${year}`} className="inbud-numeric-cell">
                                                                            {formatFigure(groupData[year], formatToFixedMoneyWithLocale, {
                                                                                precision: 1,
                                                                                suffix: '%',
                                                                            })}
                                                                        </td>
                                                                    );
                                                                })}
                                                            </Fragment>
                                                        );
                                                    })}
                                                    <td>
                                                        <div className="tw-flex tw-items-center tw-gap-x-3">
                                                            <TextButton variant="primary" onClick={(e) => editPayrate(payrate)}>
                                                                <EditIcon className="tw-w-3 tw-h-3" />
                                                            </TextButton>
                                                            <TextButton variant="danger" onClick={(e) => deletePayrate(payrate)}>
                                                                <TrashIcon className="tw-w-3 tw-h-3" />
                                                            </TextButton>
                                                        </div>
                                                    </td>
                                                </tr>
                                            );
                                        })}
                                    </tbody>
                                </table>
                            </div>
                        )}
                    </div>
                </div>
            </div>

            <PayrateModal
                modalId="save-payrate-modal"
                modalRef={cratePayrateModalRef}
                modalElement={cratePayrateModalElement}
                saveUrl={saveUrl()}
                defaultPercentage={defaultPrecentagesValue()}
                onClose={closeModal}
                onCancel={closeModal}
                payrate={editedPayrate}
                onSuccess={() => {
                    closeModal();
                    props.fetchPayrates();
                }}
                eventStartDate={props.eventStartDate}
                eventEndDate={props.eventEndDate}
            />

            <SimpleConfirmationModal
                closeHandler={() => closeModal()}
                allowAction={true}
                modalId={'delete-payrate-modal'}
                modalRef={deletePayrateModalRef}
                modalElement={deletePayrateModalElement}
                confirmationMsg={$t('Are you sure you want to delete this payrate?')}
                title={$t('Delete Payrate')}
                cancelButtonText={$t('Close')}
                primaryButtonText={$t('Delete')}
                successHandler={() => {
                    removePayrate();
                }}
            />
        </div>
    );
}
